import axios from "../../helpers/bearerAxios";
import { getContractApiErrorMessage } from "../../helpers/contractApiErrorMessage";
import { pricingPayloadRequiresSolarMetrics } from "../../helpers/financeSelectors";
import { logger } from "../loggingService";
import type { ChangeOrderLoanStatusRow } from "../../store/oneButtonStore";
import { useOneButtonStore } from "../../store/oneButtonStore";

// Base URL for the OneButton Contract API, retrieved from environment variables
const baseUrl = import.meta.env.VITE_ONEBUTTON_CONTRACT_API_URL_V2 || '';
// Base URL without path (for RetrievePriceQuote and other endpoints that use /api/Contract/...)
const contractBaseUrl = import.meta.env.VITE_ONEBUTTON_CONTRACT_BASE_URL || baseUrl.replace(/\/api\/Contract.*$/i, '') || '';

/** Fetch quotes associated with a specific Salesforce ID
 * @param salesForceId The Salesforce ID to fetch quotes for
 * @returns Quotes response
 */
const getQuotes = async (salesForceId: string) => {
    try {        
        const url = `${baseUrl}/GetQuotes?salesforceId=${salesForceId}`;        
        const response = await axios.get(url);
        return response;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

/** * Fetch quote details by quote ID
 * @param quoteId The ID of the quote
 * @returns Quote details response
 */
const getQuoteDetails = async (quoteId: string) => {
    try {        
        const url = `${baseUrl}/GetQuoteDetails?quoteId=${quoteId}`;        
        const response = await axios.get(url);
        return response;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

/** Payload for Pricing API (param names match API) */
export interface PricingPayload {
    state: string;
    systemSize: number;
    estimatedAnnualProduction: number;
    utilityCompanyCode: string;
    batteryCount: number;
    roofPitch: string;
    /** Roof squares - low slope (e.g. flat) */
    lowRoof: number;
    /** Roof squares - standard/normal slope */
    standardRoof: number;
    FinancePartner?: string;
    TreeCutCost?: number;
    /** Active opportunity product types sent to Pricing API (e.g. Solar, Roof). */
    products?: string[];
}

/** In‑flight cache so identical Pricing payloads share a single HTTP call. */
const pricingInFlight: Record<string, Promise<any>> = {};

/** * Get pricing information based on the provided payload
 * @param pricingPayload The payload containing necessary information for pricing
 * @returns Pricing response
 */
const getPricing = async (pricingPayload: PricingPayload | any) => {
    if (pricingPayloadRequiresSolarMetrics(pricingPayload)) {
        const est = Number(pricingPayload?.estimatedAnnualProduction);
        if (!Number.isFinite(est) || est <= 0) {
            throw new Error('Estimated annual production must be greater than 0.');
        }
        const size = Number(pricingPayload?.systemSize);
        if (!Number.isFinite(size) || size <= 0) {
            throw new Error('System size must be greater than 0.');
        }
    }
    const url = `${baseUrl}/Pricing`;
    const cacheKey = JSON.stringify(pricingPayload ?? {});

    const existing = pricingInFlight[cacheKey];
    if (existing) return existing;

    const request = axios
        .post(url, pricingPayload)
        .catch((error) => {
            logger.error(error);
            throw error;
        })
        .finally(() => {
            delete pricingInFlight[cacheKey];
        });

    pricingInFlight[cacheKey] = request;
    return request;
};

/** Response shape for RetrievePriceQuote (subset we use) */
export interface RetrievePriceQuoteResponse {
    RoofSquareOverride?: number | null;
    Normal_Slope_Override?: number | null;
    Low_Slope_Override?: number | null;
    QuoteVersion?: unknown[];
    Opportunity_Id?: string | number;
    SalesForce_Id?: string;
    [key: string]: any;
}

/** Response shape for ProductTypeAdder (subset we use) */
export interface ProductTypeAdder {
  Product_Type_Adder_Id: number;
  Product_Type_Adder_Name: string;
  Product_Type_Id: number;
  Product_Type: 'Roof' | 'Battery' | 'Solar' | string;
  Unit: string | null;
  Measure: string | null;
  Unit_Price: number | null;
  Free_Quantity: number | null;
  Is_Custom_Adder: boolean | null;
  Notes: string | null;
  Active_Ind: boolean;
  Adder_Commissionable: boolean | null;
}

/** Response shape for DealOpportunityProductAdders (subset we use) */
export interface DealOpportunityProductAdder {
  Deal_Opportunity_Product_Adder_Id: number;
  Opportunity_Id: number;
  Opportunity_Product_Id: number;
  Product_Type_Adder_Id: number;
  Product_Type_Id: number;
  Quantity: number;
  UnitPrice: number;
  Active_Ind: boolean;
  User_Created_Timestamp: string;
  User_Created_Id: string;
  User_Modified_Timestamp: string;
  User_Modified_Id: string;
}

/** POST `/api/Contract/SubmitVersion` — Trinity `generate-quote.service.ts` `submitVersion`. */
export interface SubmitVersionPayload {
    Opportunity_Id: string | number;
    QuoteVersion: unknown[];
    SalesForce_Id: string;
}

const submitVersion = async (payload: SubmitVersionPayload) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/SubmitVersion`;
        const response = await axios.post(url, payload);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

/** Trinity / Contract API numeric opportunity id (not Salesforce 18-char id). */
export type NumericOpportunityId = string | number;

/** POST body for `FinancePartner/contract` (parity with Trinity GenerateContractService). For `action: 'embed'`, Angular sends `salesRep` / `loggedIn` / `customer` as false. */
export interface CreateFinancePartnerContractPayload {
    quoteId: number | string;
    opportunityId: NumericOpportunityId;
    action?: string;
    salesRep?: boolean;
    loggedIn?: boolean;
    customer?: boolean;
}

/** POST `/api/Contract/ViewTrinityContract` — matches Angular `GenerateContractService.viewTrinityContract` payload. */
export interface ViewTrinityContractPayload {
    salesforceId: string;
    financePartnerQuoteId?: string | number;
    /** Extra templates beyond Combined Pack (Angular `documentTypes`); default `[]` when only Combined Pack is selected. */
    documentType?: string[] | null;
    /** Combined Pack sub-templates when expanded (Angular `selectedTemplate`). */
    selectedTemplate?: string[] | null;
    isEmail?: boolean;
    signingMethod?: string | null;
}

export interface SignFinancePartnerContractParams {
    opportunityId: NumericOpportunityId;
    leadId: string | number;
    FinancePartnerQuoteId: string | number;
    OpportunityContractId: string | number;
    isEmail: boolean;
}

const goodLeapApiRoot = (): string => {
    const root = String(contractBaseUrl || '').replace(/\/$/, '');
    if (!root) return '';
    return `${root}/api/goodLeap`;
};

/**
 * Fetch price quote / contract data by opportunity ID.
 * Used to get RoofSquareOverride, Normal_Slope_Override, Low_Slope_Override for roof square pricing.
 * @param opportunityId The opportunity ID (e.g. 1197745)
 * @returns Price quote response containing overrides
 */
const getRetrievePriceQuote = async (opportunityId: string): Promise<RetrievePriceQuoteResponse> => {
    try {
        const url = `${contractBaseUrl}/api/Contract/RetrievePriceQuote?opportunityId=${encodeURIComponent(opportunityId)}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

/* * Fetch product type adders for contract modifications.
    * @returns Array of product type adders */
const getProductTypesAdders = async (): Promise<ProductTypeAdder[]> => {
    try {
        const url = `${contractBaseUrl}/api/Contract/ProducttypesAdders`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

/**
 * Retrieves the product type adder details for a particular opportunity
 * @param opportunityId The ID of the opportunity
 * @returns An observable containing the system design losses data
 */

const DealOpportunityProductAdders = async (opportunityId: string): Promise<DealOpportunityProductAdder[]> => {
    try {
        const url = `${contractBaseUrl}/api/Contract/DealOpportunityProductAdders?opportunityId=${encodeURIComponent(opportunityId)}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};
/** Saves the product type adder details for a particular opportunity
 * @param data The data to be saved
 * @returns An observable containing the response from the API
 */

const DealSaveOpportunityProducttypesAdders = async (payload: any) => {
try {
    const url = `${contractBaseUrl}/api/Contract/DealSaveOpportunityProducttypesAdders`;
    const response = await axios.post(url, payload);
    return response?.data ?? response;
} catch (error: any) {
    logger.error(error);
    throw error;
}
};

/**
 * Full quote/contract/agreement payload for an opportunity (same as Trinity GenerateContractService.retrieveContract).
 */
const retrieveContract = async (opportunityId: string | number) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/RetrieveContract?opportunityId=${encodeURIComponent(String(opportunityId))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};



export interface LeadSyncPayload {
    finance_Partner_Lead_Sync_Id: string;
    productType: string;
    address: boolean;
    title: boolean;
    product_Category: string;
}

/**
 * Reset or Sync lead data.
 * @param opportunityId Opportunity ID
 * @param payload Array of lead sync details
 */
const resetSyncData = async (opportunityId: string, payload: LeadSyncPayload[]) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/ResetSyncData?opportunityId=${encodeURIComponent(opportunityId)}`;
        const response = await axios.post(url, payload);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

/**
 * Check sync status for an opportunity.
 */
const getSyncInfo = async (opportunityId: string) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/GetSyncInfo?opportunityId=${encodeURIComponent(opportunityId)}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

/**
 * Check if a lead exists for a specific product type.
 */
const isLeadCreated = async (opportunityId: string, productType: string) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/IsLeadCreated?opportunityId=${encodeURIComponent(opportunityId)}&productType=${encodeURIComponent(productType)}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

/**
 * Manually trigger update of finance partner leads.
 */
const updateFinancePartnerLead = async (opportunityId: string) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/UpdateFinancePartnerLead?opportunityId=${encodeURIComponent(opportunityId)}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

/**
 * Create a new lead for a specific product.
 */
const createLead = async (payload: any) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/CreateLead`;
        const response = await axios.post(url, payload);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw new Error(getContractApiErrorMessage(error, 'Failed to create lead'));
    }
};

/**
 * Check if a contact is created for a specific product type.
 */
const isContactCreated = async (opportunityId: string, productType: string) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/IsContactCreated?opportunityId=${encodeURIComponent(opportunityId)}&productType=${encodeURIComponent(productType)}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

/**
 * Trigger contact/account mapping for finance partner.
 */
const createContactFinancePartner = async (opportunityId: string, productType: string, onSync = true, onExistingLead = false) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/CreateContactFinancePartner?opportunityId=${encodeURIComponent(opportunityId)}&productType=${encodeURIComponent(productType)}&onSync=${onSync}&onExistingLead=${onExistingLead}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

/**
 * Run finance partner title check.
 */
const financePartnerTitleCheck = async (opportunityId: string, productType: string) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/FinancePartnerTitleCheck?opportunityId=${encodeURIComponent(opportunityId)}&productType=${encodeURIComponent(productType)}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

/**
 * Run credit rollover/synchronization for solar products.
 */
const creditRollover = async (opportunityId: string) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/CreditRollover?opportunityId=${encodeURIComponent(opportunityId)}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

/**
 * Search for a lead using finance partner credentials.
 */
const financePartnerLeadSearch = async (
    opportunityId: string,
    accountId: string,
    financePartnerId: string
): Promise<{ IsSuccessful?: boolean; Message?: string }> => {
    try {
        const url = `${contractBaseUrl}/api/Contract/FinancePartnerLeadSearch?opportunityId=${encodeURIComponent(opportunityId)}&accountId=${encodeURIComponent(accountId)}&financePartnerId=${encodeURIComponent(financePartnerId)}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

/**
 * Update utility data for finance partner sync.
 * @param opportunityId Opportunity ID
 * @param isRoofOnly Whether it is a Roof Only order
 * @param productType Product type (e.g., S, SB, SR)
 */
const updateUtilitiesSunnova = async (opportunityId: string, isRoofOnly: boolean, productType: string) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/UpdateUtilitiesSunnova?Id=${encodeURIComponent(opportunityId)}&RO=${isRoofOnly}&ProductType=${encodeURIComponent(productType)}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

/**
 * Final step: Create the lead system record.
 */
const createLeadSystem = async (payload: any) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/CreateLeadSystem`;
        const response = await axios.post(url, payload);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

/**
 * Check if the title verification can be ignored (Multi-contract/Roof only cases).
 */
const checkIgnoreTitle = async (opportunityId: string) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/CheckIgnoreTitle?opportunityId=${encodeURIComponent(opportunityId)}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};
const updateGoodLeapTotalSaleAmount = async (payload:any) =>{
    try {
        const url = `${contractBaseUrl}/api/goodLeap/Pricing`;
        const response = await axios.post(url, payload);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
}

const getRateMinMaxForLightreach = async (payload: any) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/RateMinMax`;
        const response = await axios.post(url, payload);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateQuoteService.getEligibleProductForLightreach` — LightreachPricingByAccount */
const getLightreachPricingByAccount = async (opportunityId: string | number, leadId: string | number) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/LightreachPricingByAccount?opportunityId=${encodeURIComponent(String(opportunityId))}&leadId=${encodeURIComponent(String(leadId))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateQuoteService.getPriceCaps` for LightReach. */
const getPriceCaps = async (leadId: string | number) => {
    try {
        const encodedLeadId = encodeURIComponent(String(leadId));
        const candidates = [
            `${contractBaseUrl}/api/Contract/getPriceCaps?leadId=${encodedLeadId}`,
            `${contractBaseUrl}/api/Contract/GetPriceCaps?leadId=${encodedLeadId}`,
        ];

        let lastError: any = null;
        for (const url of candidates) {
            try {
                const response = await axios.get(url);
                return response?.data ?? response;
            } catch (error: any) {
                lastError = error;
            }
        }
        throw lastError || new Error('Unable to fetch LightReach price caps.');
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateQuoteService.IgsCapsDataDetails` — MarketCapData */
const getIgsMarketCapData = async (opportunityId: string | number) => {
    try {
        const encodedOpportunityId = encodeURIComponent(String(opportunityId));
        const candidates = [
            `${contractBaseUrl}/api/Contract/MarketCapData?opportunityId=${encodedOpportunityId}`,
            `${contractBaseUrl}/api/Contract/marketCapData?opportunityId=${encodedOpportunityId}`,
        ];

        let lastError: any = null;
        for (const url of candidates) {
            try {
                const response = await axios.get(url);
                return response?.data ?? response;
            } catch (error: any) {
                lastError = error;
            }
        }
        throw lastError || new Error('Unable to fetch IGS market cap data.');
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

export type CalculateIgsEpcPayload = {
    opportunityId: number;
    escalator: number;
    annualProduction: number;
    systemSize: number;
    ecChecked: boolean;
    rate: number;
    monthlyPayment: number;
    stateCode: string;
    Finance_partner_contract_Type: string;
};

/** Trinity `GenerateQuoteService.IgsEPCCalaculate` — CalculateIGSEPC */
const calculateIgsEpc = async (payload: CalculateIgsEpcPayload) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/CalculateIGSEPC`;
        const response = await axios.post(url, payload);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

const GetOneDrawDesignReadiness = async (opportunityId:any) =>{
    try {
        const url = `${contractBaseUrl}/api/Contract/GetOneDrawDesignReadiness?opportunityId=${opportunityId}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
}

const savePriceAndQuote = async (data:any, ProductTypeName: string) =>{
    try {
        const url = `${baseUrl}/SavePriceAndQuote?product_category=${ProductTypeName}`;
        const response = await axios.post(url, data);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }

}

const priceCheck= async (data:any) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/PriceCheck`;
        const response = await axios.post(url, data);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
  }

const ChangeOrderOffer = async (opportunityId: any) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/ChangeOrderOffer?opportunityId=${opportunityId}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

const getQuoteGroupID = async (opportunityId: any) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/GetQuoteGroupID?opportunityId=${opportunityId}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
}

/**
 * Create finance-partner contract artifacts (Trinity: GenerateContractService.createContractFinancePartnerAPI).
 * POST `/api/Contract/FinancePartner/contract`
 */
const createContractFinancePartner = async (payload: CreateFinancePartnerContractPayload) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/FinancePartner/contract`;
        const response = await axios.post(url, payload);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

const viewTrinityContract = async (payload: ViewTrinityContractPayload) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/ViewTrinityContract`;
        const response = await axios.post(url, payload);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

const signFinancePartnerContract = async (params: SignFinancePartnerContractParams) => {
    try {
        const q = new URLSearchParams({
            opportunityId: String(params.opportunityId),
            leadId: String(params.leadId),
            FinancePartnerQuoteId: String(params.FinancePartnerQuoteId),
            OpportunityContractId: String(params.OpportunityContractId),
            isEmail: String(params.isEmail),
        });
        const url = `${contractBaseUrl}/api/Contract/SignFincancePartnerContract?${q.toString()}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

const cancelChangeOrder = async (opportunityId: NumericOpportunityId, quoteId: string | number) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/CancelChangeOrder?opportunityId=${encodeURIComponent(String(opportunityId))}&quoteId=${encodeURIComponent(String(quoteId))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

const postChangeOrder = async (opportunityId: NumericOpportunityId, projectId: string | number) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/PostChangeOrder?opportunityId=${encodeURIComponent(String(opportunityId))}&projectId=${encodeURIComponent(String(projectId))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

const getChangeOrder = async (leadQuoteId: string | number) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/GetChangeOrder?leadQuoteId=${encodeURIComponent(String(leadQuoteId))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

const getPatchChangeOrder = async (leadQuoteId: string | number, originalQuoteId: string | number) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/PatchChangeOrder?leadQuoteId=${encodeURIComponent(String(leadQuoteId))}&OriginalQuoteId=${encodeURIComponent(String(originalQuoteId))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

const sfdcSyncLead = async (
    opportunityId: NumericOpportunityId,
    financePartnerQuoteId: string | number,
    quoteGroupId?: string | number | null,
) => {
    try {
        let url = `${contractBaseUrl}/api/Contract/SFDC_Sync_Lead?opportunityId=${encodeURIComponent(String(opportunityId))}&financePartnerQuoteId=${encodeURIComponent(String(financePartnerQuoteId))}`;
        if (quoteGroupId != null && quoteGroupId !== '') {
            url += `&QuoteGroupId=${encodeURIComponent(String(quoteGroupId))}`;
        }
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

const sfdcSyncContactInformation = async (
    opportunityId: NumericOpportunityId,
    financePartnerQuoteId: string | number,
    quoteGroupId?: string | number | null,
) => {
    try {
        let url = `${contractBaseUrl}/api/Contract/SFDC_Sync_ContactInformation?opportunityId=${encodeURIComponent(String(opportunityId))}&financePartnerQuoteId=${encodeURIComponent(String(financePartnerQuoteId))}`;
        if (quoteGroupId != null && quoteGroupId !== '') {
            url += `&QuoteGroupId=${encodeURIComponent(String(quoteGroupId))}`;
        }
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

const sfdcSyncContractSingle = async (opportunityId: NumericOpportunityId, financePartnerQuoteId: string | number) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/SFDC_Sync_Contract_Single?opportunityId=${encodeURIComponent(String(opportunityId))}&financePartnerQuoteId=${encodeURIComponent(String(financePartnerQuoteId))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

const getAgreementSigningDetails = async (agreementId: string) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/GetSigningDetails?agreementId=${encodeURIComponent(agreementId)}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

const updateAgreementSignDetails = async (agreementId: string) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/UpdateAgreementDetails?agreementId=${encodeURIComponent(agreementId)}`;
        const response = await axios.post(url, null);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

const retrieveCombinedPackTemplates = async (params: {
    salesforceId: string;
    financePartnerQuoteId: string | number;
    agreementType: string;
}) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/GetTemplates?salesforceId=${encodeURIComponent(params.salesforceId)}&financePartnerQuoteId=${encodeURIComponent(String(params.financePartnerQuoteId))}&agreementType=${encodeURIComponent(params.agreementType)}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

const getCombinedPackHistory = async (params: { salesforceId: string; financePartnerQuoteId: string | number }) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/GetCombinedPackHistory?financePartnerQuoteId=${encodeURIComponent(String(params.financePartnerQuoteId))}&salesforceId=${encodeURIComponent(params.salesforceId)}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

const goodLeapCreateChangeOrder = async (opportunityId: NumericOpportunityId, opportunityContractId: string | number) => {
    try {
        const root = goodLeapApiRoot();
        const url = `${root}/CreateChangeOrder?opportunityId=${encodeURIComponent(String(opportunityId))}&opportunityContractId=${encodeURIComponent(String(opportunityContractId))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

const goodLeapDirectChangeOrder = async (opportunityId: NumericOpportunityId, opportunityContractId: string | number) => {
    try {
        const root = goodLeapApiRoot();
        const url = `${root}/DirectChangeOrder?opportunityId=${encodeURIComponent(String(opportunityId))}&opportunityContractId=${encodeURIComponent(String(opportunityContractId))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

const goodLeapUpdateChangeOrder = async (opportunityId: NumericOpportunityId, quoteId: string | number) => {
    try {
        const root = goodLeapApiRoot();
        const url = `${root}/UpdateChangeOrder?opportunityId=${encodeURIComponent(String(opportunityId))}&quoteId=${encodeURIComponent(String(quoteId))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateContractService.CheckSunnovaContractSignedStatus` — nudges backend then `retrieveContractDetails` in Angular. */
const checkContractSignedStatus = async (opportunityContractId: string | number, isRetry = true) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/CheckContractSignedStatus?opportunityContractId=${encodeURIComponent(String(opportunityContractId))}&isRetry=${encodeURIComponent(String(isRetry))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateQuoteService.getLoanStatus` — `getLoanStatus?opportunityId=` on Contract API (`getUpdateLoanStatus` in Angular). */
const contractGetLoanStatusByOpportunity = async (opportunityId: NumericOpportunityId) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/getLoanStatus?opportunityId=${encodeURIComponent(String(opportunityId))}`;
        const response = await axios.get(url);
        const raw = response?.data ?? response;
        const normalized: ChangeOrderLoanStatusRow[] = Array.isArray(raw)
            ? (raw as ChangeOrderLoanStatusRow[])
            : raw != null && typeof raw === 'object'
              ? [raw as ChangeOrderLoanStatusRow]
              : [];
        useOneButtonStore.getState().setChangeOrderDetails(normalized.length > 0 ? normalized : []);
        return raw;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateQuoteService.UpdateChecklist` — POST `SavePageChanges` */
const savePageChanges = async (payload: {
    opportunityId: NumericOpportunityId;
    pageName: string;
    changes: string;
}) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/SavePageChanges`;
        const response = await axios.post(url, payload);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateQuoteService.getCheckList` — GET `GetPageChanges?opportunityId=` */
const getPageChanges = async (opportunityId: NumericOpportunityId) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/GetPageChanges?opportunityId=${encodeURIComponent(String(opportunityId))}`;
        const response = await axios.get(url);
        const raw = response?.data ?? response;
        useOneButtonStore.getState().setCheckListDetails(raw.length > 0 ? raw : []);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateQuoteService.UpdateLoanStatus` — GET `UpdateLoanStatus?opportunityId=&field=&quoteId=` (same as Angular `environment.contractApiUrl`). */
const contractUpdateLoanStatus = async (opportunityId: NumericOpportunityId, field: string, quoteId: string | number) => {
    try {
        const url = `${contractBaseUrl}/api/Contract/UpdateLoanStatus?opportunityId=${encodeURIComponent(String(opportunityId))}&field=${encodeURIComponent(String(field))}&quoteId=${encodeURIComponent(String(quoteId))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateContractService.getLoanStatus` — refreshes GoodLeap loan row (includes application link). */
const goodLeapGetLoanStatus = async (opportunityId: NumericOpportunityId, contractId: string | number) => {
    try {
        const root = goodLeapApiRoot();
        const url = `${root}/getLoanStatus?opportunityID=${encodeURIComponent(String(opportunityId))}&contractId=${encodeURIComponent(String(contractId))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateContractService.ResendEmail` — resend GoodLeap application link email. */
const goodLeapResendEmail = async (opportunityId: NumericOpportunityId, opportunityContractId: string | number) => {
    try {
        const root = goodLeapApiRoot();
        const url = `${root}/ResendEmail?opportunityId=${encodeURIComponent(String(opportunityId))}&opportunityContractId=${encodeURIComponent(String(opportunityContractId))}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

/** Trinity `GenerateContractService.loanApplication` — submit GoodLeap “loan share” / application request. */
export type GoodLeapLoanApplicationPayload = {
    opportunityId: string | number;
    opportunityContractId: string | number;
    isEmail: boolean;
    email_Override: string;
    submitting_User_First_Name: string;
    submitting_User_Last_Name: string;
    submitting_User_Email: string;
};

const goodLeapLoanApplication = async (payload: GoodLeapLoanApplicationPayload) => {
    try {
        const root = goodLeapApiRoot();
        const url = `${root}/loanApplication`;
        const response = await axios.post(url, payload);
        return response?.data ?? response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

// Exporting the quote services as an object for use in other parts of the application
const quoteServices = {
    getQuotes,
    getQuoteDetails,
    getPricing,
    getRetrievePriceQuote,
    getProductTypesAdders,
    DealOpportunityProductAdders,
    DealSaveOpportunityProducttypesAdders,
    submitVersion,
    retrieveContract,
    resetSyncData,
    getSyncInfo,
    isLeadCreated,
    updateFinancePartnerLead,
    createLead,
    isContactCreated,
    createContactFinancePartner,
    financePartnerTitleCheck,
    creditRollover,
    financePartnerLeadSearch,
    updateUtilitiesSunnova,
    createLeadSystem,
    checkIgnoreTitle,
    updateGoodLeapTotalSaleAmount,
    getRateMinMaxForLightreach,
    getLightreachPricingByAccount,
    getPriceCaps,
    getIgsMarketCapData,
    calculateIgsEpc,
    GetOneDrawDesignReadiness,
    getQuoteGroupID,
    savePriceAndQuote,
    priceCheck,
    ChangeOrderOffer,
    createContractFinancePartner,
    viewTrinityContract,
    signFinancePartnerContract,
    cancelChangeOrder,
    postChangeOrder,
    getChangeOrder,
    getPatchChangeOrder,
    sfdcSyncLead,
    sfdcSyncContactInformation,
    sfdcSyncContractSingle,
    getAgreementSigningDetails,
    updateAgreementSignDetails,
    retrieveCombinedPackTemplates,
    getCombinedPackHistory,
    goodLeapCreateChangeOrder,
    goodLeapDirectChangeOrder,
    goodLeapUpdateChangeOrder,
    goodLeapGetLoanStatus,
    checkContractSignedStatus,
    contractGetLoanStatusByOpportunity,
    goodLeapResendEmail,
    goodLeapLoanApplication,
    contractUpdateLoanStatus,
    savePageChanges,
    getPageChanges,
    getSyncStatusList: async (opportunityId: string) => {
        try {
            const url = `${contractBaseUrl}/api/Contract/GetSyncStatusList?opportunityId=${encodeURIComponent(opportunityId)}`;
            const response = await axios.get(url);
            return response?.data ?? response;
        } catch (error: any) {
            logger.error(error);
            throw error;
        }
    }
};

export default quoteServices;